
import React from 'react';
import { OrderCheckIn } from '../OrderCheckIn';
import { ProductList } from '../ProductList';
import { PriceCheck } from '../PriceCheck';
import { BackendDashboard } from '../BackendDashboard';
import { OrderEdit } from '../OrderEdit';
import { EmployeeClockIn } from '../employee/mobile/EmployeeClockIn';
import { EmployeeManagement } from '../employee/admin/EmployeeManagement';
import { EmployeeTimeCalendar } from '../employee/time/EmployeeTimeCalendar';
import { PayrollProcessing } from '../PayrollProcessing';
import { EmployeeProfile } from '../employee/profile/EmployeeProfile';
import { FullScreenScanner } from '../mobile/FullScreenScanner';
import { AdminHeader } from '../dashboard/AdminHeader';
import { DashboardHeader } from '../dashboard/DashboardHeader';
import { useAuth } from '../auth/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { Order } from "@/hooks/useOrdersManagement/types";

type ViewType = "products" | "prices" | "backend" | "edit-order" | "employee-clockin" | "employee-management" | "employee-time-calendar" | "payroll-processing" | "employee-profile" | "credit-reports" | "cost-monitor" | "login-attempts" | "store-station" | "scan";

interface ViewRouterProps {
  currentView: ViewType;
  selectedOrder: Order | null;
  userName: string | null;
  networkStatus: "checking" | "verified" | "failed";
  onBack: () => void;
  onSave: () => void;
  onDelete: () => void;
  onSubmit: (orderData: any) => void;
  onViewTimeCalendar: () => void;
  onNetworkCheck: () => void;
  onNewOrder: () => void;
  onPriceCheck: () => void;
  onEmployeeClockIn: () => void;
  onEmployeeManagement: () => void;
  onViewBackend: () => void;
  onCreditReports: () => void;
  onCostMonitor: () => void;
  setCurrentView: (view: ViewType | "dashboard") => void;
  onLoginAttempts: () => void;
}

export const ViewRouter = ({
  currentView,
  selectedOrder,
  userName,
  networkStatus,
  onBack,
  onSave,
  onDelete,
  onSubmit,
  onViewTimeCalendar,
  onNetworkCheck,
  onNewOrder,
  onPriceCheck,
  onEmployeeClockIn,
  onEmployeeManagement,
  onViewBackend,
  onCreditReports,
  onCostMonitor,
  setCurrentView,
  onLoginAttempts
}: ViewRouterProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user is admin (has Supabase auth) vs staff (SMS auth)
  const isAdmin = !!user;

  const renderView = () => {
    switch (currentView) {
      case 'products':
        return selectedOrder ? (
          <ProductList
            order={selectedOrder}
            onBack={onBack}
          />
        ) : null;
      case 'prices':
        return <PriceCheck onBack={onBack} />;
      case 'backend':
        return <BackendDashboard onBack={onBack} />;
      case 'edit-order':
        return selectedOrder ? (
          <OrderEdit
            order={selectedOrder}
            onBack={onBack}
            onSave={onSave}
            onDelete={onDelete}
          />
        ) : null;
      case 'employee-clockin':
        return <EmployeeClockIn onBack={onBack} />;
      case 'employee-management':
        return (
          <EmployeeManagement
            onBack={onBack}
            onViewTimeCalendar={onViewTimeCalendar}
          />
        );
      case 'employee-time-calendar':
        return <EmployeeTimeCalendar onBack={onBack} />;
      case 'payroll-processing':
        return <PayrollProcessing />;
      case 'employee-profile':
        return <EmployeeProfile />;
      case 'scan':
        return (
          <FullScreenScanner
            isOpen={true}
            onClose={onBack}
            onScanResult={(barcode) => {
              console.log('Scanned:', barcode);
              onBack();
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {isAdmin ? (
        <AdminHeader
          userName={userName}
          networkStatus={networkStatus}
          onNetworkCheck={onNetworkCheck}
          onNewOrder={onNewOrder}
          onPriceCheck={onPriceCheck}
          onEmployeeClockIn={onEmployeeClockIn}
          onEmployeeManagement={onEmployeeManagement}
          onViewBackend={onViewBackend}
          onScannerTest={() => {}}
          onSwitchToStaff={() => setCurrentView("dashboard")}
          onSuppliersManagement={() => {}}
          onCreditReports={onCreditReports}
          onCostMonitor={onCostMonitor}
          onMasterProducts={() => navigate('/master-products')}
          onLoginAttempts={onLoginAttempts}
        />
      ) : (
        <DashboardHeader />
      )}
      {renderView()}
    </div>
  );
};
